import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldCheck,
  FileText,
  Star,
  UploadSimple,
  DownloadSimple,
  Trash,
  ArrowClockwise,
  CheckCircle,
  XCircle,
  Prohibit,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getGradeStyle } from "./gradeStyles";

const DOC_TYPES = [
  { key: "gst", label: "GSTIN", placeholder: "e.g., 29ABCDE1234F1Z5" },
  { key: "fssai", label: "FSSAI License", placeholder: "14-digit number" },
  { key: "msme", label: "MSME / Udyam", placeholder: "e.g., UDYAM-KA-01-0000000" },
  { key: "pan", label: "PAN", placeholder: "e.g., ABCDE1234F" },
];

const ONBOARDING_STATES = [
  { key: "draft", label: "Draft", color: "bg-slate-100 text-slate-700", Icon: FileText },
  { key: "pending_approval", label: "Pending Approval", color: "bg-amber-50 text-amber-700", Icon: Warning },
  { key: "approved", label: "Approved", color: "bg-emerald-50 text-emerald-700", Icon: CheckCircle },
  { key: "rejected", label: "Rejected", color: "bg-red-50 text-red-700", Icon: XCircle },
  { key: "blocked", label: "Blocked", color: "bg-slate-900 text-white", Icon: Prohibit },
];

function daysUntil(iso) {
  if (!iso) return null;
  const now = new Date();
  const target = new Date(iso);
  const diff = Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
}

function ExpiryChip({ iso }) {
  const d = daysUntil(iso);
  if (d === null) return null;
  const cls =
    d < 0
      ? "bg-red-50 text-red-700 border-red-200"
      : d <= 30
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";
  const label = d < 0 ? `Expired ${-d}d ago` : d === 0 ? "Expires today" : `${d}d left`;
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${cls}`}>{label}</span>
  );
}

/** Overall health of one compliance document, for the status chip + summary strip. */
function docStatus(entry, hasExpiry) {
  const hasNumber = !!entry?.number;
  const hasDoc = !!entry?.doc_name;
  if (!hasNumber && !hasDoc) {
    return { label: "Missing", cls: "bg-slate-100 text-slate-500" };
  }
  const d = hasExpiry ? daysUntil(entry?.expiry) : null;
  if (d !== null && d < 0) return { label: "Expired", cls: "bg-red-50 text-red-700" };
  if (d !== null && d <= 30) return { label: "Expiring soon", cls: "bg-amber-50 text-amber-700" };
  return { label: "Valid", cls: "bg-emerald-50 text-emerald-700" };
}

function ComplianceRow({ vendorId, docType, entry, onChange }) {
  const [num, setNum] = useState(entry?.number || "");
  const [expiry, setExpiry] = useState(entry?.expiry || "");
  const fileRef = useRef(null);
  const hasDoc = !!entry?.doc_name;

  useEffect(() => {
    setNum(entry?.number || "");
    setExpiry(entry?.expiry || "");
  }, [entry?.number, entry?.expiry]);

  const saveMeta = async () => {
    try {
      await api.put(`/vendors/${vendorId}/compliance/${docType.key}`, { number: num, expiry });
      toast.success(`${docType.label} saved`);
      onChange?.();
    } catch {
      toast.error("Save failed");
    }
  };

  const uploadDoc = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("number", num || "");
    fd.append("expiry", expiry || "");
    try {
      await api.post(`/vendors/${vendorId}/compliance/${docType.key}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${docType.label} uploaded`);
      onChange?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    }
  };

  const downloadDoc = async () => {
    try {
      const res = await api.get(`/vendors/${vendorId}/compliance/${docType.key}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = entry?.doc_name || `${docType.key}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const deleteDoc = async () => {
    if (!window.confirm(`Delete ${docType.label} document?`)) return;
    try {
      await api.delete(`/vendors/${vendorId}/compliance/${docType.key}`);
      toast.success("Deleted");
      onChange?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const status = docStatus(entry, docType.key !== "pan");

  return (
    <div
      className="px-5 py-4 space-y-3"
      data-testid={`compliance-row-${docType.key}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
            {docType.label}
          </span>
          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${status.cls}`}>
            {status.label}
          </span>
          {hasDoc && <ExpiryChip iso={entry.expiry} />}
        </div>
        {hasDoc && (
          <div className="flex items-center gap-1">
            <button
              onClick={downloadDoc}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
              title="Download"
              data-testid={`download-${docType.key}`}
            >
              <DownloadSimple size={14} />
            </button>
            <button
              onClick={deleteDoc}
              className="p-1.5 rounded hover:bg-red-50 text-red-600"
              title="Delete"
              data-testid={`delete-${docType.key}`}
            >
              <Trash size={14} />
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Number</label>
          <Input
            value={num}
            onChange={(e) => setNum(e.target.value)}
            placeholder={docType.placeholder}
            className="h-9 mt-1"
            data-testid={`input-${docType.key}-number`}
          />
        </div>
        {docType.key !== "pan" && (
          <div className="w-40">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Expiry</label>
            <Input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="h-9 mt-1"
              data-testid={`input-${docType.key}-expiry`}
            />
          </div>
        )}
        <button
          onClick={saveMeta}
          className="h-9 text-xs font-semibold text-blue-700 hover:underline"
          data-testid={`save-${docType.key}`}
        >
          Save details
        </button>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept=".pdf,image/*"
          onChange={(e) => uploadDoc(e.target.files?.[0])}
          data-testid={`file-${docType.key}`}
        />
        <button
          onClick={() => fileRef.current?.click()}
          className="h-9 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 border border-slate-200 rounded hover:bg-slate-50"
          data-testid={`upload-${docType.key}`}
        >
          <UploadSimple size={12} weight="bold" />
          {hasDoc ? "Replace" : "Upload"}
        </button>
        {hasDoc && (
          <span className="text-[10px] text-slate-500 truncate max-w-[10rem]" title={entry.doc_name}>
            {entry.doc_name}
          </span>
        )}
      </div>
    </div>
  );
}

function StarPicker({ value, onChange, disabled }) {
  return (
    <div className="flex items-center gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          disabled={disabled}
          onClick={() => onChange(s)}
          className="p-0.5"
          data-testid={`star-${s}`}
          type="button"
        >
          <Star
            size={20}
            weight={value >= s ? "fill" : "regular"}
            className={value >= s ? "text-amber-500" : "text-slate-300"}
          />
        </button>
      ))}
    </div>
  );
}

export default function VendorCompliancePanel({ vendor, onChange, readOnly = false }) {
  const [refreshing, setRefreshing] = useState(false);
  const [ratingStars, setRatingStars] = useState(vendor?.manual_rating?.stars || 0);
  const [ratingComment, setRatingComment] = useState(vendor?.manual_rating?.comment || "");

  useEffect(() => {
    setRatingStars(vendor?.manual_rating?.stars || 0);
    setRatingComment(vendor?.manual_rating?.comment || "");
  }, [vendor?.manual_rating?.stars, vendor?.manual_rating?.comment]);

  const refreshScore = async () => {
    setRefreshing(true);
    try {
      await api.post(`/vendors/${vendor.id}/refresh-score`);
      toast.success("Score refreshed");
      onChange?.();
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const saveRating = async () => {
    try {
      await api.post(`/vendors/${vendor.id}/rating`, {
        stars: ratingStars,
        comment: ratingComment,
      });
      toast.success("Rating saved");
      onChange?.();
    } catch {
      toast.error("Save failed");
    }
  };

  const setOnboarding = async (target) => {
    try {
      await api.post(`/vendors/${vendor.id}/onboarding-status`, { status: target });
      toast.success(`Onboarding → ${target.replace("_", " ")}`);
      onChange?.();
    } catch {
      toast.error("Failed");
    }
  };

  const auto = vendor.auto_score || {};
  const grade = auto.grade || "—";
  const scoreVal = auto.value ?? null;
  const gradeStyle = getGradeStyle(grade);
  const currentOnboarding = vendor.onboarding_status || "draft";
  const docSummary = DOC_TYPES.reduce((acc, d) => {
    const st = docStatus(vendor.compliance?.[d.key], d.key !== "pan");
    acc[st.label] = (acc[st.label] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="vendor-compliance-panel">
      {readOnly && (
        <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="vendor-compliance-readonly">
          View only. Compliance documents, onboarding and ratings can be changed by the Purchase team or an admin.
        </p>
      )}
      {/* Onboarding + auto-score header */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} weight="duotone" className="text-blue-600" />
            <span className="text-sm font-semibold text-slate-900">Onboarding & Score</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {ONBOARDING_STATES.map((s) => {
              const active = currentOnboarding === s.key;
              const Icon = s.Icon;
              return (
                <button
                  key={s.key}
                  onClick={() => setOnboarding(s.key)}
                  className={
                    "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition " +
                    (active
                      ? `${s.color} border-transparent`
                      : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50")
                  }
                  data-testid={`onboarding-set-${s.key}`}
                >
                  <Icon size={12} weight="bold" />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Auto Score</div>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-slate-900" data-testid="auto-score-value">
                {scoreVal ?? "—"}
              </span>
              <span className="text-sm font-semibold text-slate-400">/ 100</span>
              <span
                className={`px-2 py-0.5 rounded text-xs font-bold ${gradeStyle.chip}`}
                data-testid="auto-score-grade"
              >
                {grade}
              </span>
            </div>
            {scoreVal !== null && (
              <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${gradeStyle.bar}`}
                  style={{ width: `${Math.min(100, Math.max(0, scoreVal))}%` }}
                />
              </div>
            )}
            {auto.breakdown && (
              <div className="mt-3 space-y-1.5">
                {Object.entries(auto.breakdown).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-[11px] text-slate-600">
                    <span className="capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="tabular-nums">
                      {v.score} / {v.weight}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={refreshScore}
              disabled={refreshing}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:underline disabled:opacity-50"
              data-testid="refresh-score"
            >
              <ArrowClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing…" : "Refresh from live data"}
            </button>
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Manual Override</div>
            <StarPicker value={ratingStars} onChange={setRatingStars} />
            <Textarea
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              placeholder="Reason / notes (optional)"
              rows={2}
              className="mt-2 text-sm"
              data-testid="rating-comment"
            />
            <button
              onClick={saveRating}
              className="mt-2 inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
              data-testid="save-rating"
            >
              Save rating
            </button>
            {vendor.manual_rating?.rated_at && (
              <div className="mt-2 text-[10px] text-slate-500">
                Last rated by {vendor.manual_rating.rated_by} · {new Date(vendor.manual_rating.rated_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compliance docs */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <FileText size={16} weight="duotone" className="text-emerald-600" />
          <span className="text-sm font-semibold text-slate-900">
            Statutory & Food-safety Documents
          </span>
          <span className="text-[11px] text-slate-500 ml-2">
            (PDF or image, max 8&nbsp;MB — required for ISO&nbsp;22000 evidence)
          </span>
        </div>
        <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2 flex-wrap">
          {Object.entries(docSummary).map(([label, count]) => {
            const cls =
              label === "Missing"
                ? "bg-slate-200 text-slate-600"
                : label === "Expired"
                ? "bg-red-100 text-red-700"
                : label === "Expiring soon"
                ? "bg-amber-100 text-amber-700"
                : "bg-emerald-100 text-emerald-700";
            return (
              <span key={label} className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${cls}`}>
                {count} {label}
              </span>
            );
          })}
        </div>
        <div className="divide-y divide-slate-100">
          {DOC_TYPES.map((d) => (
            <ComplianceRow
              key={d.key}
              vendorId={vendor.id}
              docType={d}
              entry={vendor.compliance?.[d.key]}
              onChange={onChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
